import classNames from 'classnames';
import type { FocusTargetOrFalse } from 'focus-trap';
import { FocusTrap } from 'focus-trap-react';
import { ComponentProps, createContext, forwardRef, ReactNode, useId } from 'react';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import useAnimateInOutComponent from '@alltrails/core/hooks/useAnimateInOutComponent';
import useScreenLock from '@alltrails/core/hooks/useScreenLock';
import { Size } from '@alltrails/denali/types';
import { ModalAriaLabelledByProvider } from './ModalAriaLabelledByContext';
import styles from './styles/Modal.module.scss';

type ModalProps = {
  ariaLabel?: string;
  backgroundClassName?: string;
  children: ReactNode;
  focusTrapOptions?: ComponentProps<typeof FocusTrap>['focusTrapOptions'];
  initialFocus?: FocusTargetOrFalse;
  isFullHeight?: boolean;
  isDeviceHeight?: boolean;
  isOpen: boolean;
  isPaused?: boolean;
  modalClassName?: string;
  onBackRequest?: () => void;
  onCloseComplete?: () => void;
  onCloseRequest?: () => void;
  size?: Size<'sm' | 'md' | 'lg' | 'xl'>;
};

export const ModalContext = createContext<{
  isOpen: boolean;
  onBackRequest?: () => void;
}>({
  isOpen: false,
  onBackRequest: () => {}
});

// https://www.figma.com/design/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=3084-40503
const MultiStepModal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      ariaLabel,
      children,
      focusTrapOptions,
      initialFocus,
      isFullHeight,
      isDeviceHeight,
      isOpen,
      isPaused = false,
      modalClassName,
      backgroundClassName,
      onBackRequest,
      onCloseComplete,
      onCloseRequest,
      size = 'md'
    },
    ref
  ) => {
    const { isAnimating, isVisible, onAnimationEnd } = useAnimateInOutComponent(isOpen, onCloseComplete);
    useScreenLock(isVisible);
    const ariaLabelledById = useId();

    if (!isAnimating && !isVisible) {
      return null;
    }

    return (
      <ModalPortal>
        <div
          className={classNames(styles.background, backgroundClassName, {
            [styles.isAnimating]: isAnimating,
            [styles.isVisible]: isVisible
          })}
          onAnimationEnd={onAnimationEnd}
          id="modalBackground" // Needed by legacy hooks and components
          ref={ref}
        >
          <FocusTrap
            active={isVisible}
            paused={isPaused}
            focusTrapOptions={{
              clickOutsideDeactivates: true,
              escapeDeactivates: () => {
                onCloseRequest?.();
                return false;
              },
              initialFocus,
              ...focusTrapOptions
            }}
          >
            <div
              className={classNames(styles.modal, styles[size], modalClassName, {
                [styles.isFullHeight]: isFullHeight,
                [styles.isDeviceHeight]: isDeviceHeight
              })}
              role="dialog"
              aria-modal="true"
              {...(ariaLabel ? { 'aria-label': ariaLabel } : { 'aria-labelledby': ariaLabelledById })}
              onAnimationEnd={e => e.stopPropagation()}
            >
              <ModalContext.Provider
                value={{
                  isOpen,
                  onBackRequest
                }}
              >
                {ariaLabel ? children : <ModalAriaLabelledByProvider ariaLabelledBy={ariaLabelledById}>{children}</ModalAriaLabelledByProvider>}
              </ModalContext.Provider>
            </div>
          </FocusTrap>
        </div>
      </ModalPortal>
    );
  }
);

export default MultiStepModal;
